"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useWebSocket } from "@/hooks/useWebSocket";
import { isAuthenticated } from "@/lib/api/auth";
import { PaginatedResponse } from "@/lib/api/types";
import {
  getNotifications,
  markAllNotificationRead,
  markNotificationRead,
  type Notification,
  type NotificationReference,
} from "@/lib/api/user-notifications";
import { fromIntlLanguage } from "@/lib/i18n";
import { queryKeys } from "@/lib/query-keys";

// Re-export DTOs to preserve the public API for existing consumers
// (web-ui/src/routes/_app/notifications.tsx, web-ui/lib/notification-actions.ts)
// that import `type Notification` / `type NotificationReference` from this file.
export type { Notification, NotificationReference };

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  isError: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined,
);

export function NotificationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const [isClient, setIsClient] = useState(false);
  const queryClient = useQueryClient();

  // Only run on client after hydration
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsClient(true);
  }, []);

  const authenticated = isClient && isAuthenticated();

  const notificationQueryKey = queryKeys.notifications({ language });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: notificationQueryKey,
    queryFn: () => getNotifications({ language, limit: 50 }),
    enabled: authenticated,
    staleTime: 0,
  });

  // Notifications come directly from the query cache.
  // WebSocket and optimistic mutations use queryClient.setQueryData to update them.
  const notifications = data?.data ?? [];
  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!authenticated) return;
      // Optimistic update in cache
      queryClient.setQueryData<PaginatedResponse<Notification>>(
        notificationQueryKey,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((n) =>
              n.id === id ? { ...n, is_read: true, unread_count: 0 } : n,
            ),
          };
        },
      );
      try {
        await markNotificationRead(id, language);
      } catch (error) {
        console.error("[Notifications] Failed to mark as read:", error);
        void refetch(); // revert optimistic update on error
      }
    },
    [language, authenticated, queryClient, notificationQueryKey, refetch],
  );

  const markAllAsRead = useCallback(async () => {
    if (!authenticated) return;
    // Optimistic update in cache
    queryClient.setQueryData<PaginatedResponse<Notification>>(
      notificationQueryKey,
      (old) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map((n) => ({
            ...n,
            is_read: true,
            unread_count: 0,
          })),
        };
      },
    );
    try {
      await markAllNotificationRead(language);
    } catch (error) {
      console.error("[Notifications] Failed to mark all as read:", error);
      void refetch(); // revert optimistic update on error
    }
  }, [language, authenticated, queryClient, notificationQueryKey, refetch]);

  // Handle WebSocket messages — prepend new notifications directly into cache
  type NotificationWsMessage = { type: string; payload?: unknown };

  const handleMessage = useCallback(
    (message: NotificationWsMessage) => {
      if (message.type !== "notification") return;
      if (!message.payload || typeof message.payload !== "object") return;

      console.log("[Notifications] Received:", message.payload);
      const newNotification: Notification = {
        ...(message.payload as Omit<Notification, "is_read">),
        is_read: false,
      };
      queryClient.setQueryData<PaginatedResponse<Notification>>(
        notificationQueryKey,
        (old) => {
          if (!old) return old;
          return { ...old, data: [newNotification, ...old.data] };
        },
      );
    },
    [queryClient, notificationQueryKey],
  );

  // Connect WebSocket only when authenticated
  const { isConnected } = useWebSocket({
    onMessage: handleMessage,
    enabled: authenticated,
  });

  // Fallback polling when WebSocket is disconnected
  useEffect(() => {
    if (!authenticated || isConnected) return;

    const interval = setInterval(() => {
      void refetch();
    }, 30_000);

    return () => clearInterval(interval);
  }, [authenticated, isConnected, refetch]);

  const value = {
    notifications,
    unreadCount,
    loading: isLoading,
    isError,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error(
      "useNotifications must be used within NotificationProvider",
    );
  }
  return ctx;
}
